import { useMemo, useEffect, useState } from 'react'
// import FestivalLogos from 'components/FestivalLogos'

import FestivalTickerStrip from 'components/FestivalTickerStrip'
import { type Festival, fetchFestivalsFromSanity } from 'services/MovieApi'
import { formatLocalDate } from '@/libs/utils'

const FESTIVALS: Festival[] = [
  {
    name: 'Festival de Cannes',
    city: 'Cannes',
    country: 'France',
    start: '2025-05-13',
    end:   '2025-05-24',
    website: 'https://www.festival-cannes.com/',
    badge: 'Festival',
    notes: 'Book a meeting with our team at the Marché.'
  },
  {
    name: 'MIPCOM – Cannes',
    city: 'Cannes',
    country: 'France',
    start: '2025-10-13',
    end:   '2025-10-16',
    website: 'https://www.mipcom.com/',
    badge: 'Market',
    notes: 'We’ll be taking meetings at the Palais des Festivals throughout market days.'
  },
  {
    name: 'TIFF – Toronto International Film Festival',
    city: 'Toronto',
    country: 'Canada',
    start: '2025-09-04',
    end:   '2025-09-14',
    website: 'https://tiff.net/',
    badge: 'Festival',
    notes: 'We’ll be taking meetings throughout Industry days.'
  },
  {
    name: 'Busan International Film Festival',
    city: 'Busan',
    country: 'South Korea',
    start: '2025-10-02',
    end:   '2025-10-11',
    website: 'https://www.biff.kr',
    badge: 'Festival',
    notes: 'Major Asian market presence; strong industry attendance.'
  },
  {
    name: 'AFM – American Film Market',
    city: 'Los Angeles',
    country: 'United States',
    start: '2025-11-11',
    end:   '2025-11-16',
    website: 'https://americanfilmmarket.com/',
    badge: 'Market',
    notes: 'Meetings at the Fairmont Century Plaza; screenings at AMC Century City 15.'
  }
]



function statusFor(f: Festival) {
  const today = new Date()
  const s = new Date(f.start)
  const e = new Date(f.end + 'T23:59:59')
  if (today < s) return 'Upcoming'
  if (today > e) return 'Past'
  return 'Happening Now'
}

export default function Festivals() {
  const [sanityFestivals, setSanityFestivals] = useState<Festival[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const loadFestivals = async () => {
      try {
        const festivals = await fetchFestivalsFromSanity()
        setSanityFestivals(festivals)
      } catch (error) {
        console.error('Failed to fetch festivals from Sanity:', error)
      } finally {
        setIsLoading(false)
      }
    }
    loadFestivals()
  }, [])

  // Merge hardcoded and Sanity festivals
  const allFestivals = useMemo(() => {
    // Deduplicate by name (prefer Sanity over hardcoded if same name)
    const festivalMap = new Map<string, Festival>()

    // Add hardcoded festivals first
    FESTIVALS.forEach(f => festivalMap.set(f.name.toLowerCase(), f))

    // Override or add Sanity festivals
    sanityFestivals.forEach(f => festivalMap.set(f.name.toLowerCase(), f))

    return Array.from(festivalMap.values())
  }, [sanityFestivals])

  const grouped = useMemo(() => {
    const byStatus: Record<string, Festival[]> = { 'Happening Now': [], Upcoming: [], Past: [] }
    allFestivals.forEach(f => byStatus[statusFor(f)].push(f))
    // sort upcoming by soonest, past by most recent
    byStatus['Upcoming'].sort((a,b)=>+new Date(a.start)-+new Date(b.start))
    byStatus['Past'].sort((a,b)=>+new Date(b.end)-+new Date(a.end))
    return byStatus
  }, [allFestivals])

  const Section = ({title, items}:{title:string;items:Festival[]}) => {
    if (!items.length) return null
    return (
      <section className="mb-10">
        <h2 className="text-2xl md:text-3xl font-bold mb-4 text-slate-900 dark:text-slate-100">{title}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((f,i)=>(
            <article key={f.name+i}
                     className="rounded-2xl border border-slate-200/60 dark:border-slate-800 bg-white/60 dark:bg-slate-900/40 backdrop-blur-sm p-5 hover:shadow-md transition">
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{f.name}</h3>
                <span className="text-xs px-2 py-1 rounded-full bg-blue-600/10 text-blue-700 dark:text-blue-300">
                  {f.badge ?? 'Festival'}
                </span>
              </div>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                {f.city}, {f.country}
              </p>
              <p className="mt-2 text-sm font-medium text-slate-800 dark:text-slate-200">
                {formatLocalDate(f.start)} – {formatLocalDate(f.end)}
              </p>
              {f.notes && (
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{f.notes}</p>
              )}
              <div className="mt-4 flex items-center gap-2">
                {f.website && (
                  <a
                    href={f.website}
                    target="_blank" rel="noopener noreferrer"
                    className="text-sm font-semibold text-blue-700 dark:text-blue-300 hover:underline"
                  >
                    Official site →
                  </a>
                )}
                <a
                  href={`mailto:sales@bluefoxentertainment.com?subject=Meeting at ${encodeURIComponent(f.name)}`}
                  className="ml-auto inline-flex items-center text-sm px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                >
                  Book a meeting
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>
    )
  }

  return (
    <div className="min-h-screen bg-background-light dark:bg-background">
      <header className="relative container mx-auto px-6 pt-10 pb-6 overflow-hidden">


        <h1 className="relative z-10 text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100">
          Festivals & Markets
        </h1>
        <p className="relative z-10 mt-3 text-slate-600 dark:text-slate-400 max-w-2xl">
          Come meet the Blue Fox team at upcoming festivals and markets. We’d love to talk acquisitions,
          sales, and partnerships.
        </p>
        <br/>
        {/* Logos strip */}
        {/*<FestivalLogos className="mt-6" height={66} />*/}
      <FestivalTickerStrip/>
      </header>
      <br/>



      <main className="container mx-auto px-6 pb-16">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            <Section title="Happening Now" items={grouped['Happening Now']} />
            <Section title="Upcoming" items={grouped['Upcoming']} />
            <Section title="Past" items={grouped['Past']} />
          </>
        )}
      </main>
    </div>
  )
}
